import { Injectable, OnDestroy } from '@angular/core';
import { AuthStateService } from '../core/states/auth-state.service';
import { environment } from '../../environments/environment';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import Echo from 'laravel-echo';
import Pusher from 'pusher-js';

// Declarar Pusher globalmente para que Echo lo encuentre
(window as any).Pusher = Pusher;

export interface WebSocketMessage {
  type: 'new_message' | 'typing';
  data: any;
}

@Injectable({
  providedIn: 'root'
})
export class WebSocketService implements OnDestroy {
  private echo: Echo<any> | null = null;
  private messageSubject = new Subject<WebSocketMessage>();
  private connectionStatus = new BehaviorSubject<boolean>(false);
  private subscribedChannels: Map<string, any> = new Map();

  constructor(private authService: AuthStateService) {
    this.initializeEcho();
  }

  /**
   * Inicializa la conexion Echo con Reverb
   */
  private initializeEcho(): void {
    const token = this.authService.currentUserValue?.token;

    if (!token) {
      console.warn('WebSocket: No hay token de autenticacion');
      return;
    }

    const useTLS = environment.REVERB_SCHEME === 'https';

    this.echo = new Echo({
      broadcaster: 'reverb',
      key: environment.REVERB_APP_KEY,
      wsHost: environment.REVERB_HOST,
      wsPort: useTLS ? 443 : environment.REVERB_PORT,
      wssPort: useTLS ? 443 : environment.REVERB_PORT,
      forceTLS: useTLS,
      encrypted: useTLS,
      disableStats: true,
      enabledTransports: useTLS ? ['wss'] : ['ws'],
      authEndpoint: environment.REVERB_AUTH_ENDPOINT,
      auth: {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json',
        }
      }
    });

    // Monitorear estado de conexion
    this.echo.connector.pusher.connection.bind('connected', () => {
      console.log('WebSocket: Conectado');
      this.connectionStatus.next(true);
    });

    this.echo.connector.pusher.connection.bind('disconnected', () => {
      console.log('WebSocket: Desconectado');
      this.connectionStatus.next(false);
    });

    this.echo.connector.pusher.connection.bind('error', (error: any) => {
      console.error('WebSocket Error:', error);
      this.connectionStatus.next(false);
    });
  }

  /**
   * Observable del estado de conexion
   */
  get isConnected$(): Observable<boolean> {
    return this.connectionStatus.asObservable();
  }

  /**
   * Observable de mensajes entrantes
   */
  get messages$(): Observable<WebSocketMessage> {
    return this.messageSubject.asObservable();
  }

  /**
   * Suscribirse a un canal de chat privado
   */
  subscribeToChat(chatId: number): void {
    if (!this.echo) {
      console.warn('WebSocket: Echo no inicializado');
      this.initializeEcho();
      if (!this.echo) return;
    }

    const channelName = `private-chat.${chatId}`;

    // Evitar suscripciones duplicadas
    if (this.subscribedChannels.has(channelName)) {
      console.log(`WebSocket: Ya suscrito a ${channelName}`);
      return;
    }

    const channel = this.echo.private(channelName);

    // Escuchar nuevos mensajes
    channel.listen('.message.new', (data: any) => {
      console.log('WebSocket: Nuevo mensaje recibido', data);
      this.messageSubject.next({
        type: 'new_message',
        data: data
      });
    });

    // Escuchar indicador de escritura
    channel.listen('.user.typing', (data: any) => {
      console.log('WebSocket: Usuario escribiendo', data);
      this.messageSubject.next({
        type: 'typing',
        data: data
      });
    });

    this.subscribedChannels.set(channelName, channel);
    console.log(`WebSocket: Suscrito a ${channelName}`);
  }

  /**
   * Desuscribirse de un canal de chat
   */
  unsubscribeFromChat(chatId: number): void {
    const channelName = `private-chat.${chatId}`;

    if (this.echo && this.subscribedChannels.has(channelName)) {
      this.echo.leave(channelName);
      this.subscribedChannels.delete(channelName);
      console.log(`WebSocket: Desuscrito de ${channelName}`);
    }
  }

  /**
   * Reconectar (util cuando cambia el token)
   */
  reconnect(): void {
    this.disconnect();
    this.initializeEcho();
  }

  /**
   * Desconectar completamente
   */
  disconnect(): void {
    if (this.echo) {
      // Desuscribirse de todos los canales
      this.subscribedChannels.forEach((_, channelName) => {
        this.echo?.leave(channelName);
      });
      this.subscribedChannels.clear();

      this.echo.disconnect();
      this.echo = null;
      this.connectionStatus.next(false);
    }
  }

  ngOnDestroy(): void {
    this.disconnect();
    this.messageSubject.complete();
    this.connectionStatus.complete();
  }
}
